import React, { useEffect, useState } from "react";

import { createGenerateClassName, StylesProvider } from "@mui/styles";

import { OnboardingStage } from "../../../../../models/onboarding.stage";
import AccessSettingsPage from "./AccessPage";
import ChecklistPage from "./ChecklistPage";
import EquipmentPage from "./FleetPage";
import InstructionsPage from "./InstructionPage";
import InventoryPage from "./InventoryPage";
import MaterialPage from "./MaterialPage";
import ServicePage from "./ServicePage";
import useStyles from "./styles";
import SubContractors from "./Subcontractors";
import { useAppState } from "Context/StateContext";
import { checkResourceCan, FN_NAMES, hasPermission } from "Util/permissions";
import { ResourceCanEnum, ResourceName } from "Util/enums/accessRule";

enum Pages {
  INVENTORY = "Inventory",
  CHECKLIST = "Checklist & Forms",
  FLEET = "Fleet",
  STAFF = "Staff",
  SERVICES = "Services",
  INSTRUCTIONS = "Instructions",
  ACCESS = "Access",
  MATERIAL = "Materials",
  PROJECTS = "Projects",
  EQUIPMENTS = "Equipments",
  SUBCONTRACTORS = "Subcontractors",
}
interface Props {
  tabName: string;
  tabSubName: string;
  onboardingStage?: OnboardingStage;
  refetchIsoCertificate: () => void;
}

const generateClassName = createGenerateClassName({
  productionPrefix: "rsc-",
  seed: "resources-pages",
});

const Resources = ({ tabName, tabSubName, onboardingStage, refetchIsoCertificate }: Props) => {
  const classes = useStyles();
  const { userPermissions } = useAppState();
  const [resourcePermission, setResourcePermission] = useState<{
    inventory: {
      edit: boolean;
      create: boolean;
    };
    service: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
    subContractor: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
    equipment: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
    checkListForm: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
    instruction: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
    material: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
    access: {
      edit: boolean;
      create: boolean;
      changeStatus: boolean;
    };
  }>({
    inventory: {
      edit: false,
      create: false,
    },
    service: {
      edit: false,
      create: false,
      changeStatus: false,
    },
    subContractor: {
      edit: false,
      create: false,
      changeStatus: false,
    },
    equipment: {
      edit: false,
      create: false,
      changeStatus: false,
    },
    checkListForm: {
      edit: false,
      create: false,
      changeStatus: false,
    },
    instruction: {
      edit: false,
      create: false,
      changeStatus: false,
    },
    material: {
      edit: false,
      create: false,
      changeStatus: false,
    },
    access: {
      edit: false,
      create: false,
      changeStatus: false,
    },
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setResourcePermission({
        inventory: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_INVENTORY_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_INVENTORY_ADD_NEW),
        },
        service: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_SERVICES_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_SERVICES_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_SERVICES_CHANGE_STATUS),
        },
        subContractor: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_SUB_CONTRACTORS_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_SUB_CONTRACTORS_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(
              userPermissions,
              FN_NAMES.FUNCTION_RESOURCES_SUB_CONTRACTORS_CHANGE_STATUS
            ),
        },
        equipment: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_EQUIPMENTS_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_EQUIPMENTS_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_EQUIPMENTS_CHANGE_STATUS),
        },
        checkListForm: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_CHECKLIST_FORMS_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_CHECKLIST_FORMS_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(
              userPermissions,
              FN_NAMES.FUNCTION_RESOURCES_CHECKLIST_FORMS_CHANGE_STATUS
            ),
        },
        instruction: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_INSTRUCTIONS_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_INSTRUCTIONS_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_INSTRUCTIONS_CHANGE_STATUS),
        },
        material: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_MATERIALS_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_MATERIALS_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_MATERIALS_CHANGE_STATUS),
        },
        access: {
          edit:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_ACCESS_EDIT),
          create:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_ACCESS_ADD_NEW),
          changeStatus:
            checkResourceCan(userPermissions, ResourceName.RESOURCES, ResourceCanEnum.EDIT) &&
            hasPermission(userPermissions, FN_NAMES.FUNCTION_RESOURCES_ACCESS_CHANGE_STATUS),
        },
      });
    }
  }, [userPermissions]);

  const renderCurrentPage = () => {
    switch (tabName) {
      case Pages.INVENTORY:
        return (
          <InventoryPage
            refetchIsoCertificate={refetchIsoCertificate}
            onboardingStage={onboardingStage}
            tabName={tabSubName}
            permission={resourcePermission.inventory}
          />
        );
      case Pages.CHECKLIST:
        return <ChecklistPage permission={resourcePermission.checkListForm} />;
      case Pages.FLEET:
      case Pages.EQUIPMENTS:
        return <EquipmentPage permission={resourcePermission.equipment} />;
      case Pages.SERVICES:
        return <ServicePage permission={resourcePermission.service} />;
      case Pages.INSTRUCTIONS:
        return <InstructionsPage permission={resourcePermission.instruction} />;
      case Pages.ACCESS:
        return <AccessSettingsPage permission={resourcePermission.access} />;
      case Pages.MATERIAL:
        return <MaterialPage permission={resourcePermission.material} />;

      case Pages.SUBCONTRACTORS:
        return <SubContractors permission={resourcePermission.subContractor} />;
      default:
    }
  };

  return (
    <StylesProvider generateClassName={generateClassName}>
      <div data-cy="resourceWrapper" className={classes.resourceWrapper}>
        {renderCurrentPage()}
      </div>
    </StylesProvider>
  );
};

export default Resources;
